import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText.js';
import { ScrambleTextPlugin } from 'gsap/ScrambleTextPlugin.js';

/** Composante TextScramble de TimTools */
export default class TextScramble {
  static instances = new Set();

  /** Remplace du texte sans reconstruire les éléments HTML. */
  static async replaceText(root, from, to, { animate = true, signal } = {}) {
    signal?.throwIfAborted();
    if (!(from instanceof RegExp) && (typeof from !== 'string' || !from)) {
      throw new Error(
        'TextScramble.replaceText attend un mot non vide ou une RegExp.',
      );
    }
    const pattern =
      from instanceof RegExp
        ? new RegExp(
            from.source,
            from.flags.includes('g') ? from.flags : `${from.flags}g`,
          )
        : new RegExp(
            `(?<![\\p{L}\\p{N}_])${from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![\\p{L}\\p{N}_])`,
            'giu',
          );

    const instances = [...TextScramble.instances].filter((instance) =>
      root.contains(instance.element),
    );
    await Promise.all(instances.map((instance) => instance.ready));
    signal?.throwIfAborted();
    const activeInstances = instances.filter((instance) => !instance.destroyed);
    const states = activeInstances.map((instance) => ({
      instance,
      progress: instance.timeline?.progress() ?? 0,
      paused: instance.timeline?.paused() ?? true,
      observing: instance.isObserved,
    }));
    // Retire le découpage et les anciennes timelines avant de modifier leur texte source.
    states.forEach((state) => {
      state.instance.restore();
      state.text = state.instance.element.textContent;
    });

    try {
      const groups = new Map();
      const excluded =
        'script, style, noscript, template, svg, canvas, textarea, input, select, code, pre, [contenteditable]:not([contenteditable="false"]), #wpadminbar';
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        const parent = node.parentElement;
        if (!parent || parent.closest(excluded)) continue;
        const block =
          parent.closest(
            'a, button, p, h1, h2, h3, h4, h5, h6, li, blockquote, figcaption, label, td, th',
          ) || parent;
        if (!groups.has(block)) groups.set(block, []);
        groups.get(block).push(node);
      }

      const animations = [];
      const reducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;
      for (const nodes of groups.values()) {
        const text = nodes.map((textNode) => textNode.data).join('');
        const matches = [...text.matchAll(pattern)];
        if (!matches.length) continue;
        let offset = 0;
        const segments = nodes.map((textNode) => {
          const segment = {
            node: textNode,
            start: offset,
            end: offset + textNode.data.length,
            text: textNode.data,
          };
          offset = segment.end;
          return segment;
        });
        // Les offsets restent ceux du texte source, même si sa longueur change.
        for (const match of matches.reverse()) {
          const start = match.index;
          const end = start + match[0].length;
          const covered = match[0].length
            ? segments.filter(
                (segment) => segment.start < end && segment.end > start,
              )
            : [
                segments.find((segment) => segment.end > start) ||
                  segments[segments.length - 1],
              ];
          let remaining = to;
          covered.forEach((segment, index) => {
            const localStart = Math.max(start - segment.start, 0);
            const localEnd = Math.min(end, segment.end) - segment.start;
            const replacement =
              index === covered.length - 1
                ? remaining
                : remaining.slice(0, localEnd - localStart);
            remaining = remaining.slice(replacement.length);
            segment.text =
              segment.text.slice(0, localStart) +
              replacement +
              segment.text.slice(localEnd);
          });
        }
        for (const segment of segments) {
          if (segment.text === segment.node.data) continue;
          signal?.throwIfAborted();
          if (!animate || reducedMotion) segment.node.data = segment.text;
          else
            animations.push(
              TextScramble.scrambleNode(segment.node, segment.text, { signal }),
            );
        }
      }
      await Promise.all(animations);
    } finally {
      // Reconstruit les animations avec le nouveau texte, y compris data-repeat.
      states.forEach((state) => {
        const { instance } = state;
        if (instance.destroyed || instance.motionPreference.matches) return;
        const changed = instance.element.textContent !== state.text;
        instance.initAnim();
        instance.timeline
          .progress(changed ? 1 : state.progress)
          .paused(changed || state.paused);
        if (changed ? instance.options.repeat : state.observing)
          instance.initObserver();
      });
    }
  }

  static async scrambleNode(
    node,
    text,
    { signal, duration = 0.8, revealDelay = 0.2 } = {},
  ) {
    signal?.throwIfAborted();
    gsap.registerPlugin(ScrambleTextPlugin);
    // Le plugin anime un élément détaché : liens, SVG et spans restent intacts.
    const proxy = document.createElement('span');
    proxy.textContent = node.data;
    return new Promise((resolve, reject) => {
      let tween;
      let updated = false;
      const cleanup = () => signal?.removeEventListener('abort', onAbort);
      const onAbort = () => {
        tween?.kill();
        cleanup();
        if (updated) node.data = text;
        reject(signal.reason);
      };
      signal?.addEventListener('abort', onAbort, { once: true });
      try {
        tween = gsap.to(proxy, {
          duration,
          scrambleText: {
            text,
            chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789',
            revealDelay,
            tweenLength: false,
            speed: 0.6,
          },
          ease: 'none',
          onUpdate: () => {
            updated = true;
            node.data = proxy.textContent;
          },
          onComplete: () => {
            cleanup();
            node.data = text;
            resolve();
          },
          onInterrupt: () => {
            cleanup();
            if (updated) node.data = text;
            reject(new DOMException('Scramble interrompu.', 'AbortError'));
          },
        });
        if (signal?.aborted) tween.kill();
      } catch (error) {
        cleanup();
        if (updated) node.data = text;
        reject(error);
      }
    });
  }
  /**
   * @param {HTMLElement} element - Élément HTML sur lequel la composante est instanciée
   */
  constructor(element) {
    gsap.registerPlugin(SplitText, ScrambleTextPlugin);

    this.element = element;
    this.options = {
      repeat: false,
      duration: 0.5,
      stagger: 0.04,
      chars: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789<>/{}[]_+',
    };
    this.destroyed = false;
    this.motionPreference = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    );

    TextScramble.instances.add(this);
    this.ready = document.fonts.ready.then(() => {
      if (!this.destroyed) this.init();
    });
  }

  init() {
    this.setOptions();
    if (this.motionPreference.matches) return;

    this.initAnim();
    this.initObserver();
  }

  setOptions() {
    this.options.repeat =
      'repeat' in this.element.dataset &&
      this.element.dataset.repeat !== 'false';

    if (this.element.dataset.scrambleChars) {
      this.options.chars = this.element.dataset.scrambleChars;
    }
  }

  initAnim() {
    // Les spans directs restent les lignes originales du blockquote.
    const lines = this.element.querySelectorAll(':scope > span');
    this.split = SplitText.create(lines.length ? lines : this.element, {
      type: 'words,chars',
      tag: 'span',
      aria: 'auto',
    });
    this.timeline = gsap.timeline({ paused: true });

    this.split.chars.forEach((char, index) => {
      const text = char.textContent;
      const fontSize = parseFloat(getComputedStyle(char).fontSize);
      // Une lettre aléatoire plus large ne doit pas décaler le reste du texte.
      char.style.width = `${char.offsetWidth / fontSize}em`;
      char.style.textAlign = 'center';
      gsap.set(char, { autoAlpha: 0 });

      const position = index * this.options.stagger;
      this.timeline.set(char, { autoAlpha: 1 }, position);
      this.timeline.to(
        char,
        {
          duration: this.options.duration,
          scrambleText: {
            text,
            chars: this.options.chars,
            revealDelay: this.options.duration * 0.5,
            tweenLength: false,
            speed: 0.6,
          },
          ease: 'none',
        },
        position,
      );
    });
  }

  initObserver() {
    this.observer = new IntersectionObserver(this.watch.bind(this), {
      threshold: 0.15,
    });
    this.observer.observe(this.element);
    this.isObserved = true;
  }

  watch(entries) {
    if (!this.timeline || this.destroyed) return;
    for (const entry of entries) {
      if (entry.isIntersecting) {
        this.animIn();
        if (!this.options.repeat) {
          this.observer.unobserve(this.element);
          this.isObserved = false;
        }
      } else if (this.options.repeat) {
        this.animOut();
      }
    }
  }

  animIn() {
    this.timeline.play(0);
  }

  animOut() {
    this.timeline.pause(0);
    gsap.set(this.split.chars, { autoAlpha: 0 });
  }

  restore() {
    this.observer?.disconnect();
    this.timeline?.kill();
    this.split?.revert();
    this.observer = null;
    this.isObserved = false;
    this.timeline = null;
    this.split = null;
  }

  destroy() {
    this.destroyed = true;
    this.restore();
    TextScramble.instances.delete(this);
  }
}
